/**
 * Calculates a CPF verification digit
 * @param digits Array of digits to calculate from
 * @param multiplierStart Starting multiplier value
 * @returns Calculated verification digit
 */
const calculateVerificationDigit = (
  digits: number[],
  multiplierStart: number,
): number => {
  const sum = digits.reduce(
    (acc, digit, index) => acc + digit * (multiplierStart - index),
    0,
  );
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

/**
 * Validates a CPF (Brazilian tax ID) number
 * @param cpf CPF string (can contain formatting characters)
 * @returns true if CPF is valid, false otherwise
 */
export const validateCPF = (cpf: string | undefined): boolean => {
  if (!cpf) {
    return false;
  }

  // Remove all non-numeric characters
  const cleanCpf = cpf.replace(/\D/g, '');

  // Check if CPF has 11 digits
  if (cleanCpf.length !== 11) {
    return false;
  }

  // Check if all digits are the same (invalid CPF)
  if (/^(\d)\1+$/.test(cleanCpf)) {
    return false;
  }

  // Convert string to array of numbers
  const digits = cleanCpf.split('').map((char) => parseInt(char, 10));

  // Calculate verification digits
  const firstNineDigits = digits.slice(0, 9);
  const digit1 = calculateVerificationDigit(firstNineDigits, 10);
  const firstTenDigits = digits.slice(0, 10);
  const digit2 = calculateVerificationDigit(firstTenDigits, 11);

  // Verify if calculated digits match the provided digits
  return digit1 === digits[9] && digit2 === digits[10];
};
